import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { TotpSetup } from '../types';
import './Setup2FA.css';

/**
 * Enrol the signed-in admin in two-factor authentication.
 *
 * Required in production: the backend gates every admin route on it and answers
 * 2FA_REQUIRED until it is on, which is what redirects an admin here. Outside
 * production the screen is an opt-in — but enrolling is never cosmetic, because
 * /auth/login challenges any enrolled account in every environment.
 *
 * There is no QR image on purpose. The portal has no QR dependency, and the
 * alternative — posting the secret to a third-party QR service — would hand the
 * TOTP seed to someone else. Manual entry and the otpauth:// link both work with
 * every authenticator app.
 */
const Setup2FA: React.FC = () => {
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [done, setDone] = useState(false);
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  const secret = setup ? new URLSearchParams(setup.uri.split('?')[1] || '').get('secret') || '' : '';

  const readError = (err: any, fallback: string) => {
    const d = err?.response?.data;
    if (typeof d?.detail === 'string') return d.detail;
    return d?.message || d?.detail?.message || fallback;
  };

  // Deliberately triggered by a click, never on mount: /auth/2fa/setup mints a
  // new secret and a new set of backup codes each time, invalidating the last.
  const begin = async () => {
    setError('');
    setBusy(true);
    try {
      setSetup(await authService.setupTotp());
    } catch (err: any) {
      setError(readError(err, 'Could not start 2FA setup.'));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await authService.confirmTotp(code.trim());
      await refreshUser();
      setDone(true);
    } catch (err: any) {
      setError(readError(err, 'That code was not accepted. Codes change every 30 seconds.'));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="tfa-page">
        <div className="tfa-card">
          <h1>Two-factor authentication is on</h1>
          <p className="tfa-lead">
            Admin endpoints are now available. From your next sign-in you will be asked
            for a code after your password.
          </p>
          <button className="tfa-btn" onClick={() => navigate('/dashboard')}>
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tfa-page">
      <div className="tfa-card">
        <h1>Set up two-factor authentication</h1>
        <p className="tfa-lead">
          Admin access in production requires 2FA. Until it is enabled there, the
          dashboard will load but every request returns <code>403</code>. Once enabled,
          every sign-in asks for a code after your password.
        </p>

        {error && <div className="tfa-error">{error}</div>}

        {!setup ? (
          <>
            <p className="tfa-note">
              You will need an authenticator app — 1Password, Authy, Google Authenticator
              or similar.
            </p>
            <button className="tfa-btn" onClick={begin} disabled={busy}>
              {busy ? 'Starting…' : 'Begin setup'}
            </button>
          </>
        ) : (
          <>
            <ol className="tfa-steps">
              <li>
                <strong>Add the account to your authenticator.</strong>
                <p className="tfa-note">
                  <a className="tfa-otpauth" href={setup.uri}>Open in authenticator app</a>
                  {' '}— or enter this key by hand:
                </p>
                <code className="tfa-secret">{secret}</code>
              </li>

              <li>
                <strong>Save your backup codes.</strong>
                <p className="tfa-note">
                  Each works once. They are the only way in if you lose the authenticator,
                  and they are shown only now.
                </p>
                <ul className="tfa-codes">
                  {setup.backup_codes.map((c) => <li key={c}><code>{c}</code></li>)}
                </ul>
                <label className="tfa-check">
                  <input
                    type="checkbox"
                    checked={saved}
                    onChange={(e) => setSaved(e.target.checked)}
                  />
                  I have saved these codes somewhere safe
                </label>
              </li>

              <li>
                <strong>Confirm with a code.</strong>
                <form onSubmit={confirm} className="tfa-confirm">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    autoComplete="one-time-code"
                    maxLength={6}
                    aria-label="Authentication code"
                  />
                  {/* Gated on `saved`: enabling 2FA without the backup codes
                      recorded is how an admin locks themselves out for good. */}
                  <button className="tfa-btn" type="submit" disabled={busy || code.length !== 6 || !saved}>
                    {busy ? 'Verifying…' : 'Enable 2FA'}
                  </button>
                </form>
                {!saved && (
                  <p className="tfa-note tfa-muted">
                    Tick the box above once your backup codes are stored.
                  </p>
                )}
              </li>
            </ol>
          </>
        )}
      </div>
    </div>
  );
};

export default Setup2FA;
